// Import jwt-decode (v4.x uses named export)
import { jwtDecode } from 'jwt-decode';

/**
 * Decode JWT token and extract payload
 * @param {string} token - JWT token
 * @returns {object|null} Decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expiring soon
 * @param {string} token - JWT token
 * @param {number} bufferMinutes - Minutes before expiry to consider "expiring soon" (default: 1)
 * @returns {boolean} True if token expires within buffer time
 */
export const isTokenExpiringSoon = (token, bufferMinutes = 1) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Date.now() / 1000; // Convert to seconds
  const expiresAt = decoded.exp;
  const bufferSeconds = bufferMinutes * 60;
  
  const willExpireSoon = (expiresAt - now) < bufferSeconds;
  
  if (willExpireSoon) {
    const remainingSeconds = Math.floor(expiresAt - now);
    console.log(`⏰ Token expiring in ${remainingSeconds} seconds`);
  }
  
  return willExpireSoon;
};

/**
 * Check if token is already expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Date.now() / 1000;
  return decoded.exp < now;
};

/**
 * Get remaining time until token expires
 * @param {string} token - JWT token
 * @returns {number} Remaining seconds (0 if expired or invalid)
 */
export const getTokenRemainingTime = (token) => {
  if (!token) return 0;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return 0;
  
  const now = Date.now() / 1000;
  const remaining = Math.max(0, decoded.exp - now);
  
  return Math.floor(remaining);
};

/**
 * Format remaining time as human-readable string
 * @param {number} seconds - Remaining seconds
 * @returns {string} Formatted time (e.g., "5m 30s")
 */
export const formatRemainingTime = (seconds) => {
  if (seconds <= 0) return 'Expired';
  
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};
